import { useEffect, useId, useState } from "react";
import { type AssetChanges, usePatchAsset } from "../api/assetlib";
import type { AssetDetail } from "../api/types";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Dialog, Field, Input, Select } from "../ui";
import { OBJECT_CATEGORIES, categoryWord } from "./categories";

const NAME_MAX = 60;
const DESCRIPTION_MAX = 200;
const TAGS_MAX = 16;
const TAG_MAX = 24;

/** The words of a tags field: lower-cased, split at anything that is not a
 *  letter or a digit, each cut to 24 characters, without empties or repeats,
 *  the first sixteen. */
export function tagWords(text: string): string[] {
  const words = text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((word) => word.slice(0, TAG_MAX))
    .filter(Boolean);
  return [...new Set(words)].slice(0, TAGS_MAX);
}

/** "Edit details": the name, the description, the tags and, for an object,
 *  the category of one of the organisation's own assets. Only what changed
 *  is sent; with nothing changed **Save** just closes. */
export function EditDetailsDialog(props: {
  open: boolean;
  onClose(): void;
  detail: AssetDetail;
  onSaved(): void;
}): JSX.Element {
  const { open, detail } = props;
  const formId = useId();
  const patch = usePatchAsset();
  const meta = detail.meta;
  const before = {
    name: String(meta.name ?? ""),
    description: String(meta.description ?? ""),
    category: String(meta.category ?? ""),
    tags: (Array.isArray(meta.tags) ? (meta.tags as string[]) : []).join(", "),
  };
  const [name, setName] = useState(before.name);
  const [description, setDescription] = useState(before.description);
  const [category, setCategory] = useState(before.category);
  const [tags, setTags] = useState(before.tags);
  const { reset } = patch;

  // Each opening starts from the asset as it is.
  useEffect(() => {
    if (!open) return;
    setName(before.name);
    setDescription(before.description);
    setCategory(before.category);
    setTags(before.tags);
    reset();
    // The fields follow the asset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const words = tagWords(tags);
  const saving = patch.isPending;

  const save = () => {
    if (saving || !name.trim() || words.length === 0) return;
    const changes: AssetChanges = {};
    if (name.trim() !== before.name) changes.name = name.trim();
    if (description.trim() !== before.description.trim()) {
      changes.description = description.trim();
    }
    if (detail.kind === "shape" && category !== before.category) {
      changes.category = category;
    }
    if (words.join(",") !== tagWords(before.tags).join(",")) {
      changes.tags = words;
    }
    if (Object.keys(changes).length === 0) {
      props.onClose();
      return;
    }
    patch.mutate(
      { kind: detail.kind, id: detail.id, changes },
      { onSuccess: props.onSaved },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) props.onClose();
      }}
      title="Edit details"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            className="disabled:cursor-not-allowed disabled:opacity-50"
            disabled={saving || !name.trim() || words.length === 0}
            aria-busy={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <Field label="Name">
          <Input
            required
            maxLength={NAME_MAX}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Description">
          <textarea
            className="tb-input"
            rows={3}
            maxLength={DESCRIPTION_MAX}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {detail.kind === "shape" ? (
          <Field label="Category">
            <Select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {OBJECT_CATEGORIES.map((id) => (
                <option key={id} value={id}>
                  {categoryWord(id)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field
          label="Tags"
          helper="Words separated by commas or spaces; at most 16."
        >
          <Input
            value={tags}
            onChange={(event) => setTags(event.target.value)}
          />
        </Field>
        {patch.error ? <ErrorLine error={patch.error} context="plain" /> : null}
      </form>
    </Dialog>
  );
}
